import React, { useEffect, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { observer, autorun } from '~/common/mobx.decorator'
import { notifyStore } from '~/stores'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { NotifyDialog } from './notify-dialog.component'
import { defaultTo } from 'lodash'
import { Typography } from '../typography'
import { Toast } from '~/components/snackbar'
import { ConnectionBanner } from './connection-banner.component'

// Older callers pass `actions: [{ label, onClick, keepSnackbar }]`; newer ones a single `action`.
export const toastActions = (options = {}, t = (value) => value) => {
	const list = [...(options.action ? [options.action] : []), ...(Array.isArray(options.actions) ? options.actions : [])]
	return list
		.filter((item) => item && item.label)
		.map(({ label, onClick, keepSnackbar, keepOpen }) => ({ label: t(label), onClick, keepOpen: Boolean(keepOpen || keepSnackbar) }))
}

// The provider's maxSnack (app.js).
export const MAX_ON_SCREEN = 3

const translate = (t, value) => (typeof value === 'string' ? t(value) : value)

/*
 * Moves messages from `notifyStore` onto the screen.
 *
 * The reaction is created once and disposed on unmount. It used to be created
 * in the render body, so every re-render of this component added one more
 * watcher that never went away.
 */
export const GlobalNotify = observer(() => {
	const { t } = useTranslation()
	const { waitDialogs, doneDialogs } = notifyStore
	const { enqueueSnackbar, closeSnackbar } = useSnackbar()
	/*
	 * What is on screen now, by kind and text. notistack's `preventDuplicate` compares
	 * keys once a key is given, so it never caught a repeat; the old code worked around
	 * that by using the message as the key (AHS-1245), which broke closing and actions.
	 */
	const visible = useRef(new Set())
	// Keys on screen, oldest first. With three up, the oldest makes room for the newest; notistack would do the
	// same for toasts that wait to be dismissed, but only after logging a warning about it.
	const onScreen = useRef([])

	useEffect(
		() =>
			autorun(() => {
				notifyStore.waitMessages.forEach(({ key, message, options = {} }) => {
					if (notifyStore.doneMessages[key]) return
					const variant = options.variant || 'info'
					const text = translate(t, message)
					const actions = toastActions(options, t)
					const showing = typeof text === 'string' ? `${variant}:${text}` : null

					// Already on screen, word for word: a second copy adds nothing.
					if (showing && visible.current.has(showing)) {
						notifyStore.closeMessage(key)
						return
					}
					if (showing) visible.current.add(showing)
					if (onScreen.current.length >= MAX_ON_SCREEN) closeSnackbar(onScreen.current.shift())
					onScreen.current.push(key)

					enqueueSnackbar(typeof text === 'string' ? text : key, {
						key,
						variant,
						persist: options.duration === null,
						autoHideDuration: options.duration === null ? undefined : options.duration,
						onExited: () => {
							if (showing) visible.current.delete(showing)
							onScreen.current = onScreen.current.filter((item) => item !== key)
						},
						content: (snackKey) => (
							<Toast
								id={snackKey}
								variant={variant}
								message={text}
								description={translate(t, options.description)}
								actions={actions}
								onClose={() => closeSnackbar(snackKey)}
								closeLabel={t('DISMISS')}
							/>
						),
					})

					notifyStore.closeMessage(key)
				})
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	return (
		<>
			<ConnectionBanner />
			{waitDialogs.map(({ key, message, options }) => (
				<NotifyDialog
					key={key}
					title={options?.title}
					okText={options?.okText}
					cancelText={options?.cancelText}
					okProps={options?.okProps}
					cancelProps={options?.cancelProps}
					open={defaultTo(doneDialogs[key], true)}
					onOk={() => {
						if (typeof options?.onOk === 'function') {
							options.onOk(key)
						}
						notifyStore.closeDialog(key)
					}}
					showButtonClose={options?.showButtonClose}
					onClose={() => options?.onClose?.(key)}
					onCancel={options?.onCancel ? () => options?.onCancel?.(key) : undefined}
				>
					{options?.children ? (
						typeof options.children === 'function' ? (
							options?.children({ key })
						) : (
							options?.children
						)
					) : (
						<Typography whiteSpace="pre-line" align="left" color="content.dark">
							{t(message)}
						</Typography>
					)}
				</NotifyDialog>
			))}
		</>
	)
})
